import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import ConfirmDialog from "./ConfirmDialog";
import { formatNumber } from "../utils/numbers";
import { TbRefresh } from "react-icons/tb";

const thCls =
  "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-r last:border-r-0";
const tdCls =
  "px-4 py-2.5 text-xs text-gray-700 border-r last:border-r-0 whitespace-nowrap";

function EditQtyModal({ item, onSave, onClose }) {
  const [qty, setQty] = useState(String(item.stock_in));
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.select();
  }, []);

  const handleSave = () => {
    const val = Number(qty);
    if (!val || val <= 0) {
      toast.warning("Quantity must be greater than 0.");
      return;
    }
    onSave(item.transaction_id, val);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-80 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-bold text-gray-800">Edit Quantity</h3>
            <p className="text-xs text-gray-400 mt-0.5 truncate">
              {item.product_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition"
          >
            &times;
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              QTY
            </label>
            <input
              ref={inputRef}
              type="number"
              min="1"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-green-400"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") onClose();
              }}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-green-700 hover:bg-green-800 transition"
            >
              Save
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ApproveStockIn() {
  const { user } = useSelector((state) => state.auth);
  const [pendingGroups, setPendingGroups] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [editItem, setEditItem] = useState(null);

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const fetchPendingStockIn = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/store/transaction/pending_stockin",
      );
      const grouped = res.data.result.reduce((acc, item) => {
        const g = item.transaction_group_id;
        if (!acc[g]) acc[g] = [];
        acc[g].push(item);
        return acc;
      }, {});
      setPendingGroups(grouped);
    } catch {
      toast.error("Failed to fetch pending Stock In records.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingStockIn();
  }, []);

  const saveQtyEdit = async (transactionId, newQty) => {
    setEditItem(null);
    try {
      await axios.patch(
        "https://eps.elkomprintama.com/api/store/stocks/pending_stockin/qty",
        {
          transaction_id: transactionId,
          stock_in: newQty,
        },
      );
      toast.success("Quantity updated!");
      fetchPendingStockIn();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to update quantity.");
      fetchPendingStockIn();
    }
  };

  const handleApprove = (groupId) => {
    setConfirmAction({ type: "approve", groupId });
    setConfirmMessage(
      "APPROVE this Stock In batch? Stock will be added to inventory.",
    );
    setShowConfirm(true);
  };

  const handleCancel = (groupId) => {
    setConfirmAction({ type: "cancel", groupId });
    setConfirmMessage("CANCEL this Stock In batch? This cannot be undone.");
    setShowConfirm(true);
  };

  const submitAction = async () => {
    if (!confirmAction) return;
    const { type, groupId } = confirmAction;
    setIsLoading(true);
    try {
      if (type === "approve") {
        await axios.put(
          "https://eps.elkomprintama.com/api/store/stocks/approve_stockin",
          { groupId, approvedBy: user.username },
        );
        toast.success("Stock In approved!");
      } else {
        await axios.put(
          "https://eps.elkomprintama.com/api/store/stocks/cancel_stockin",
          { groupId, approvedBy: user.username },
        );
        toast.success("Stock In request cancelled.");
      }
      fetchPendingStockIn();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Action failed.");
    } finally {
      setIsLoading(false);
      setConfirmAction(null);
    }
  };

  const groupEntries = Object.entries(pendingGroups);

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {isLoading && <LoadingOverlay message="Loading…" />}

      {/* Header */}
      <div className="w-full rounded-lg bg-green-700 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Approve Stock In
        </label>
      </div>

      {/* Body card */}
      <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="flex-shrink-0 px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
          <span className="text-sm font-semibold text-gray-600">
            {groupEntries.length} pending batch
            {groupEntries.length !== 1 ? "es" : ""}
          </span>
          <button
            onClick={fetchPendingStockIn}
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 bg-white border border-gray-300 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition"
          >
            <TbRefresh size={14} />
            Refresh
          </button>
        </div>

        {/* Group cards — scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
          {groupEntries.length === 0 && !isLoading && (
            <div className="h-full flex items-center justify-center text-gray-400 italic text-sm pt-16">
              No pending Stock In requests.
            </div>
          )}

          {groupEntries.map(([groupId, items]) => {
            const first = items[0];
            const totalQty = items.reduce((s, i) => s + Number(i.stock_in), 0);

            return (
              <div
                key={groupId}
                className="border border-gray-200 rounded-xl overflow-hidden shadow-sm"
              >
                {/* Card header */}
                <div className="flex flex-wrap items-center justify-between px-5 py-3 bg-green-50 border-b border-green-100">
                  <div className="text-xs text-gray-600 space-y-0.5">
                    <div>
                      <span className="font-bold text-gray-700">
                        Submitted by:
                      </span>{" "}
                      {first.transaction_by}
                    </div>
                    <div>
                      <span className="font-bold text-gray-700">Time:</span>{" "}
                      {formatDateTime(first.transaction_time)}
                    </div>
                    <div className="flex gap-4 pt-0.5">
                      <span>
                        <span className="font-bold text-gray-700">Items:</span>{" "}
                        {items.length}
                      </span>
                      <span>
                        <span className="font-bold text-gray-700">
                          Total Qty:
                        </span>{" "}
                        {formatNumber(totalQty)}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleApprove(groupId)}
                      className="px-4 py-1.5 rounded-lg text-xs font-bold text-white bg-green-700 hover:bg-green-800 transition"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleCancel(groupId)}
                      className="px-4 py-1.5 rounded-lg text-xs font-bold text-white bg-red-600 hover:bg-red-700 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>

                {/* Items table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-gray-700 text-white uppercase">
                      <tr>
                        <th className={thCls}>No</th>
                        <th className={thCls}>Product Code</th>
                        <th className={thCls}>Product Name</th>
                        <th className={thCls}>QTY</th>
                        <th className={thCls}>Supplier</th>
                        <th className={thCls}>Remark</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase whitespace-nowrap">
                          Edit
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, i) => (
                        <tr
                          key={item.transaction_id}
                          className={`border-b hover:bg-green-50 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                        >
                          <td className={`${tdCls} text-gray-400`}>{i + 1}</td>
                          <td className={tdCls}>{item.product_code}</td>
                          <td className={`${tdCls} font-medium text-gray-800`}>
                            {item.product_name}
                          </td>
                          <td className="px-4 py-2.5 border-r whitespace-nowrap">
                            <span className="font-bold text-green-700">
                              {formatNumber(item.stock_in)}
                            </span>
                          </td>
                          <td className={tdCls}>
                            {item.transaction_for || "—"}
                          </td>
                          <td className={tdCls}>
                            {item.transaction_remark || "—"}
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <button
                              onClick={() => setEditItem(item)}
                              className="text-xs font-semibold text-cyan-600 hover:text-cyan-800 transition"
                            >
                              Edit qty
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showConfirm && (
        <ConfirmDialog
          message={confirmMessage}
          onConfirm={() => {
            setShowConfirm(false);
            submitAction();
          }}
          onCancel={() => {
            setShowConfirm(false);
            setConfirmAction(null);
          }}
        />
      )}

      {editItem && (
        <EditQtyModal
          item={editItem}
          onSave={saveQtyEdit}
          onClose={() => setEditItem(null)}
        />
      )}
    </div>
  );
}

export default ApproveStockIn;
